import { normalizePluginCapabilities } from '../../../app/runtime/adminCompatibilityModel';

/**
 * readPluginLane — reads plugin-owned operator state via bridge.


 * Prefers `onboarding.state` (richer truth, shared with wizard summary).
 * Falls back to `app.bootstrap` for older plugins that do not support
 * the onboarding action.  Both paths produce the same normalized shape
 * so that composeOperatorState and all downstream consumers see a stable
 * contract regardless of plugin version.


 * @param {object} bridge - BridgeClient instance (sendMessageWithResponse)
 * @returns {Promise<object>} Normalized plugin lane result
 */
// Derive preview build status from readiness + indexing (no `ui` section)
// ---------------------------------------------------------------------------
function computePreviewBuildStatus(indexing, readiness, onboarding) {
  // When the plugin tracks preview_built explicitly (checkpoint-capable plugin),
  // use it as truth — it is more precise than can_search, which only reflects
  // whether the search index is healthy, not whether a preview bundle was built.
  // typeof guard ensures backward compat: old plugins that omit the field fall
  // through to the readiness-based derivation below.
  if (onboarding && typeof onboarding.preview_built === 'boolean') {
    if (onboarding.preview_built) return 'ready';
    // preview_built=false + job id present means a build is already in flight.
    // Return 'building' so Home doesn't regress to NOT_READY while the wizard
    // is actively polling that job in SummaryStep.
    if (onboarding.preview_build_job_id) return 'building';
    return 'none';
  }
  if (readiness.can_search === true) return 'ready';
  const s = indexing.status || 'idle';
  if (s === 'running' || s === 'queued') return 'building';
  return 'none';
}
// Normalize onboarding.state response into a stable plugin snapshot
// ---------------------------------------------------------------------------
export function normalizeOnboardingState(state) {
  const conn = state.connection || {};
  const details = conn.details || {};
  const indexing = state.indexing || {};
  const readiness = state.readiness || {};
  const onboarding = state.onboarding || {};
  const context = state.context || {};
  const caps = state.capabilities || {};
  const live = state.live && typeof state.live === 'object' ? state.live : null;

  return {
    available: true,
    source: 'onboarding.state',
    auth: {
      connected: !!conn.connected,
      hasToken: !!details.has_token,
    },
    indexing: {
      status: indexing.status || 'idle',
      progress: typeof indexing.progress === 'number' ? indexing.progress : 0,
      startedAt: indexing.started_at || null,
      finishedAt: indexing.finished_at || null,
      updatedAt: indexing.updated_at || null,
      error: indexing.error || null,
      jobId: indexing.job_id || null,
    },
    activeIndexPresent: typeof indexing.active_index_present === 'boolean'
      ? indexing.active_index_present
      : null,
    // Bridge v2+ authoritative readiness signal
    canSearch: readiness.can_search === true,
    previewBuildStatus: computePreviewBuildStatus(indexing, readiness, onboarding),
    // 4 +— publishState from bridge. camelCase per ,
    // BridgeUseCase emits JSON via json_encode (camelCase convention). The
    // capability flag below (`caps.publish_state_v1`) is a separate namespace
    // and remains snake_case by convention.
    publishState: state.publishState ?? null,
    liveState: live ? {
      enabled: live.enabled === true,
      reason: typeof live.reason === 'string' ? live.reason : null,
    } : null,
    onboarding: {
      wizardCompleted: !!onboarding.wizard_completed,
      modeSelected: !!onboarding.mode_selected,
      integrationStepCompleted: !!onboarding.integration_step_completed,
      previewBuilt: !!onboarding.preview_built,
      selectedExperience: typeof onboarding.selected_experience === 'string'
        ? onboarding.selected_experience
        : null,
    },
    context: {
      platform: context.platform || details.platform || null,
      moduleVersion: context.module_version || details.module_version || null,
      shopId: context.shop_id || details.shop_id || null,
      shopUrl: context.shop_url || context.shopUrl || null,
      adminLanguageId: context.language_id == null ? null : String(context.language_id),
    },
    capabilities: normalizePluginCapabilities(caps),
  };
}
// Normalize bootstrap response into a stable plugin snapshot
// ---------------------------------------------------------------------------
export function normalizeBootstrap(response) {
  const auth = response.auth || {};
  const indexing = response.indexing || {};
  const context = response.context || {};
  const services = response.services || {};

  return {
    available: true,
    source: 'app.bootstrap',
    auth: {
      connected: !!auth.connected,
      hasToken: !!auth.has_token,
    },
    indexing: {
      status: indexing.status || 'idle',
      progress: typeof indexing.progress === 'number' ? indexing.progress : 0,
      startedAt: indexing.started_at || null,
      finishedAt: indexing.finished_at || null,
      updatedAt: indexing.updated_at || null,
      error: indexing.error || null,
      jobId: indexing.job_id || null,
    },
    activeIndexPresent: null,
    previewBuildStatus: null,
    publishState: null,
    liveState: null,
    onboarding: null,
    context: {
      platform: context.platform || null,
      moduleVersion: context.module_version || null,
      shopId: context.shop_id || null,
      shopUrl: context.shop_url || context.shopUrl || null,
      adminLanguageId: context.language_id == null ? null : String(context.language_id),
    },
    capabilities: deriveBootstrapCapabilities(services, response),
  };
}
// Derive capability flags from what the current bootstrap exposes
// ---------------------------------------------------------------------------
function deriveBootstrapCapabilities(services, response) {
  const features = services.features || [];
  const actions = services.supported_actions || [];

  const caps = response.capabilities || {};

  return normalizePluginCapabilities(caps, {
    operatorStateV1: !!(response.auth && response.indexing),
    indexProgressV1: response.indexing
      ? typeof response.indexing.progress === 'number'
      : false,
    onboardingCheckpointsV1: false,
    analytics: features.includes('analytics') || actions.includes('analytics'),
    synonyms: features.includes('synonyms') || actions.includes('synonyms'),
    facets: features.includes('facets') || actions.includes('facets'),
    index: features.includes('index') || actions.includes('index'),
  });
}
// Degraded result when bridge is unavailable
// ---------------------------------------------------------------------------
export function degradedPluginLane(error) {
  return {
    available: false,
    source: null,
    error: {
      code: error?.errorCode || error?.code || 'BRIDGE_UNAVAILABLE',
      message: error?.message || error?.error || 'Bridge communication failed',
    },
    auth: { connected: false, hasToken: false },
    indexing: {
      status: 'idle',
      progress: 0,
      startedAt: null,
      finishedAt: null,
      updatedAt: null,
      error: null,
      jobId: null,
    },
    activeIndexPresent: null,
    previewBuildStatus: null,
    publishState: null,
    liveState: null,
    onboarding: null,
    context: {
      platform: null, moduleVersion: null, shopId: null, adminLanguageId: null,
    },
    capabilities: normalizePluginCapabilities(),
  };
}
// Main reader — prefers onboarding.state, falls back to app.bootstrap
// ---------------------------------------------------------------------------
export async function readPluginLane(bridge) {
  try {
    // 1. Try richer onboarding.state first
    const onboardingResponse = await bridge.sendMessageWithResponse('onboarding.state', {});
    if (onboardingResponse.result !== 'error') {
      const state = onboardingResponse.state || onboardingResponse;
      return normalizeOnboardingState(state);
    }
    const bootstrapResponse = await bridge.sendMessageWithResponse('app.bootstrap', {});
    if (bootstrapResponse.result === 'error') {
      return degradedPluginLane(bootstrapResponse);
    }
    return normalizeBootstrap(bootstrapResponse);
  } catch (err) {
    return degradedPluginLane(err);
  }
}

/** Normalize the product-neutral response returned by runtime operator.state. */
export function normalizeRuntimeOperatorLane(response) {
  if (!response || response.result === 'error') {
    return degradedPluginLane(response || new Error('Operator state is unavailable'));
  }
  if (response.state && typeof response.state === 'object') {
    return normalizeOnboardingState(response.state);
  }
  return normalizeBootstrap(response);
}
